import { useDeleteDepartment } from '@/departments/useDepartments';
import { useOrg } from '@/organization/useOrg';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { useOrganizationDepartments } from '@/organization/useOrganizations';
import { Department } from '@/types/department';
import { getErrorMessage } from '@/util/errorUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import DepartmentEditModal from './DepartmentEditModal';

export default function DepartmentsPage() {
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id || '';
  // Fetch departments and user's role
  const { data: departments, isLoading: departmentsLoading } =
    useOrganizationDepartments(orgId);
  const { data: perms, isLoading: permLoading } = useOrganizationPerms(orgId);

  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [deletingDept, setDeletingDept] = useState<Department | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize delete mutation
  const deleteDepartment = useDeleteDepartment(orgId ?? '');

  const canManageDepts = perms?.canManageDepts ?? false;
  const isLoading = departmentsLoading || permLoading;

  const handleDeleteConfirm = () => {
    if (!deletingDept?.id) return;

    deleteDepartment.mutate(deletingDept.id, {
      onSuccess: () => {
        setDeletingDept(null);
        setError(null);
      },
      onError: (error: unknown) => {
        console.error('Failed to delete department:', error);
        setError(getErrorMessage(error));
        setDeletingDept(null);
      },
    });
  };

  if (!selectedOrg) {
    return (
      <Container>
        <Box sx={{ mb: 3 }} />
        <Paper sx={{ p: 4 }}>
          <Typography variant='body1' color='text.primary' align='center'>
            No organization was selected, please join or select an organization
            to view departments.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg'>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant='h4'>Departments</Typography>
        {/* Only show Add Department button to admins */}
        {canManageDepts && (
          <Button variant='contained' onClick={() => setAddOpen(true)}>
            Add Department
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity='error' onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  {/* Only show Actions column to admins */}
                  {canManageDepts && (
                    <TableCell align='right'>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {departments?.map((dept: Department) => (
                  <TableRow key={dept.id}>
                    <TableCell>{dept.name}</TableCell>
                    <TableCell>{dept.description}</TableCell>
                    {/* Only show action buttons to admins */}
                    {canManageDepts && (
                      <TableCell align='right'>
                        <Tooltip title={'Edit'}>
                          <IconButton
                            color={'info'}
                            onClick={() => setEditingDept(dept)}
                            size='small'
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={'Delete'}>
                          <IconButton
                            onClick={() => setDeletingDept(dept)}
                            size='small'
                            color='error'
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {departments?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canManageDepts ? 3 : 2} align='center'>
                      No departments found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Only render modals if user is an admin */}
      {canManageDepts && (
        <>
          {/* Add/Edit Modal */}
          {(addOpen || editingDept) && (
            <DepartmentEditModal
              open={addOpen || Boolean(editingDept)}
              department={editingDept ?? undefined}
              orgId={orgId}
              onClose={() => {
                setAddOpen(false);
                setEditingDept(null);
              }}
            />
          )}

          {/* Delete Confirmation */}
          <ConfirmDialog
            open={Boolean(deletingDept)}
            title='Delete Department'
            message={`Delete department "${deletingDept?.name}" This cannot be undone.`}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeletingDept(null)}
          />
        </>
      )}
    </Container>
  );
}
